import { useContext, useEffect, useState } from "react";
import { AccountContext } from "../../AccountContext";
import { useAccountById, useCurrentBillingPeriod, useGetAccruedInterestForBillingPeriod, useGetLedger } from "@app/sdk";
import {  toCurrency, toFormattedDate } from "@app/utilities";
import styled from "styled-components";
import Modal  from "react-overlays/Modal";
import { JsonViewer } from "@textea/json-viewer";
import React from "react";


const BreakdownModal = styled(Modal)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
  z-index: 1040;
  background-color: white;
  width: 100%;
  height: 100%;
  padding: 20px;
  background-color: rgba(0,0,0,0.2);
`;

export default function AccrualsSummary() {
    const { selectedBillingPeriod, accountId } = useContext(AccountContext);
    const { data: account } = useAccountById(accountId);
    const [breakdownModalVisible, setBreakdownModalVisible] = useState(false);
    const [showBreakdownJson, setShowBreakdownJson] = useState(false);
    const [groupBy, setGroupBy] = useState(false);
    const { data: currentBillingPeriod } = useCurrentBillingPeriod(accountId);
    const [billingPeriodId, setBillingPeriodId] = useState<number | undefined>();
    const [, setHoveredRowKey] = useState<string | undefined>();
    const [selectedBreakdown, setSelectedBreakdowns] = useState<any>(); 
    const { data: accruedInterestResponse, refetch: refetchAccruedInterest, isFetching, error } = useGetAccruedInterestForBillingPeriod({ accountId, billingPeriodId });
    const [groupedBySubledger, setGroupedBySubledger] = useState<any[]>([]);
    const [groupedByDate, setGroupedByDate] = useState<any[]>([]);
    const [hideEmptySubledgers, setHideEmptySubledgers] = useState(false);
    const { data: ledgerResponse } = useGetLedger({ accountId });
   
    const closeBreakdownModal = () => {
        setBreakdownModalVisible(false);
        setSelectedBreakdowns([]);
      };
    const openBreakdownsModal = (breakdowns: any, penalty: any, title: string) => { 
        setSelectedBreakdowns({breakdowns: breakdowns, penalty: penalty, title: title});
        setBreakdownModalVisible(true);
    }

    useEffect(() => {
        setBillingPeriodId(selectedBillingPeriod.billingPeriodId);
        setTimeout(() => {
            refetchAccruedInterest();
        }, 10);
    }, [currentBillingPeriod?.billingPeriodId, selectedBillingPeriod, refetchAccruedInterest, account]);

    useEffect(() => {
        let accruedInterestValues = accruedInterestResponse;
            accruedInterestValues = {
                ...accruedInterestResponse,
                dailyAccruals: accruedInterestResponse?.dailyAccruals?.map(dailyAccrual => ({
                    ...dailyAccrual,
                    subledgers: dailyAccrual.subledgers.filter(subledger => !hideEmptySubledgers || subledger.dailyInterest?.total).map(subledger => {
                      return {
                        ...subledger,
                        spendType: ledgerResponse?.subledgers.find(ledgerSubledger => ledgerSubledger.subledgerId === subledger.subledgerId)?.spendType,
                        feeCategory: ledgerResponse?.subledgers.find(ledgerSubledger => ledgerSubledger.subledgerId === subledger.subledgerId)?.feeCategory,
                        subledgerType: ledgerResponse?.subledgers.find(ledgerSubledger => ledgerSubledger.subledgerId === subledger.subledgerId)?.subledgerType,
                      }
                    })
                }))
            };

        if (accruedInterestValues) {
            const bySubledger = accruedInterestValues?.dailyAccruals?.reduce((acc, dailyAccrual) => {
                dailyAccrual?.subledgers?.forEach(subledger => {
                    if (!acc[subledger.subledgerId]) {
                        acc[subledger.subledgerId] = [];
                    }
                    acc[subledger.subledgerId].push({
                        date: dailyAccrual.date,
                        spendType: subledger.spendType,
                        feeCategory: subledger.feeCategory,
                        subledgerType: subledger.subledgerType,
                        breakdowns: subledger.breakdowns,
                        ...subledger.dailyInterest,
                    });
                });
                return acc;
            }, {});
            if(bySubledger) {
                setGroupedBySubledger(Object.entries(bySubledger));
            }
            if(accruedInterestValues.dailyAccruals) {
                setGroupedByDate(accruedInterestValues.dailyAccruals);
            }
        }
    }, [accruedInterestResponse, hideEmptySubledgers, ledgerResponse]);

    return (
        <>
            {isFetching && <div className="ui active centered inline loader" />}
            {!isFetching && (!accruedInterestResponse || error) && <p className="ui message">No accruals</p>}
            {!isFetching && !error && accruedInterestResponse && (
                <>
                <BreakdownModal 
                    onHide={closeBreakdownModal}
                    aria-labelledby="modal-label"
                     show={breakdownModalVisible}>
                    <div className="modal-content" style={{
                        position: 'absolute',
                        left: '50%', top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '50%', height: 'auto',
                        maxHeight: '80%',
                        overflow: 'auto', 
                        boxShadow:'0 5px 15px rgba(0, 0, 0, 0.5)',
                        backgroundColor: 'white'}}>
                        <div className="ui basic segment">
                          <div className="ui basic segment" >
                            <button className="ui right floated icon button" onClick={() => closeBreakdownModal()}>
                              <i className="close icon" ></i>
                            </button>
                            <h3 className="ui left floated header center">{selectedBreakdown?.title ?? 'Breakdown'}</h3>
                          </div>
                          <button style={{marginTop: '10px'}}  className="ui button blue" onClick={() => setShowBreakdownJson(!showBreakdownJson)}> {!showBreakdownJson ? 'Show' : 'Hide'} JSON</button>
                            
                            {showBreakdownJson && <JsonViewer value={selectedBreakdown} />}
                            {selectedBreakdown?.breakdowns && <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Statemented</th>
                                    <th>Non-Statemented</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Balance</td>
                                    {selectedBreakdown.breakdowns.map((breakdown, index) => (
                                        <td key={index}>{toCurrency(breakdown.balance, 4)}</td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Adjusted Balance</td>
                                    {selectedBreakdown.breakdowns.map((breakdown, index) => (
                                        <td key={index}>{toCurrency(breakdown.adjustedBalance, 4)}</td>
                                    ))}
                                </tr>
                                <tr>
                                    <td>Accrued</td>
                                    {selectedBreakdown.breakdowns.map((breakdown, index) => (
                                        <td key={index}>{toCurrency(breakdown.accruedInterest, 4)}</td>
                                    ))}
                                </tr>
                                {(
                                    <React.Fragment>
                                        <tr>
                                            <td rowSpan={Math.max(...selectedBreakdown.breakdowns.map(b => b.adjustments.length).concat(1))}>Adjustments</td>
                                            {selectedBreakdown.breakdowns.map((breakdown, index) => (
                                                <td key={index}>
                                                    {breakdown.adjustments.length > 0 ? toCurrency(breakdown.adjustments[0].interest, 4) : "£0.00"}
                                                    {breakdown.adjustments.length > 0 ? " (Balance Type: " + breakdown.adjustments[0].balanceType : ""}
                                                    {breakdown.adjustments.length > 0 ? " - Updated Balance: " + toCurrency(breakdown.adjustments[0].updatedBalance, 4) + ")" : ""}
                                                </td>
                                            ))}
                                        </tr>
                                        {Array.from({ length: Math.max(...selectedBreakdown.breakdowns.map(b => b.adjustments.length)) - 1 }).map((_, adjIndex) => (
                                            <tr key={`adjustment-${adjIndex + 1}`}>
                                                {selectedBreakdown.breakdowns.map((breakdown, index) => (
                                                    <td style={{border: '1px solid rgba(34, 36, 38, 0.1)'}} key={index}>
                                                        {breakdown.adjustments[adjIndex + 1] ? toCurrency(breakdown.adjustments[adjIndex + 1].interest, 4) : "£0.00"}
                                                        {breakdown.adjustments[adjIndex + 1] ? " (Balance Type: " + breakdown.adjustments[adjIndex + 1].balanceType : ""}
                                                        {breakdown.adjustments[adjIndex + 1] ? " - Updated Balance: " + toCurrency(breakdown.adjustments[adjIndex + 1].updatedBalance, 4) + ")" : ""}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                )}
                                <tr>
                                    <td>Penalty</td>
                                        <td>{toCurrency(selectedBreakdown.penalty || 0, 4)}</td>
                                        <td>{toCurrency(0)}</td>
                                </tr>

                            </tbody>
                        </table>}
                        </div>
                    </div>
     
                </BreakdownModal>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
                    <button className="ui button blue" onClick={() => setGroupBy(!groupBy)}> Grouped by: {groupBy ? 'Subledger' : 'Date'}</button>
                    <button className="ui button blue" onClick={() => setHideEmptySubledgers(!hideEmptySubledgers)}> {hideEmptySubledgers ? 'Show' : 'Hide'} Rows With No Totals (£0.00)</button>
                </div>
                    <table 
                    className="ui structured striped table">
                        <thead>
                            <tr>
                                <th style={{ width: groupBy ? '450px' : '150px' }}>{groupBy ? 'Subledger' : 'Date'}</th>
                                <th style={{ width: groupBy ? '150px' : '300px' }}>{groupBy ? 'Date' : 'Subledger'}</th>
                                <th>Balance</th>
                                <th>Total</th>
                                <th>Accrued</th>
                                <th>Adjustment</th>
                                <th>Penalty</th>
                                <th style={{width: '200px'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupBy
                                ? groupedBySubledger.map(([subledgerId, entries]) => (
                                    entries.map((entry, index) => (
                                        <>
                                        <tr key={`${subledgerId}-${entry.date}`}
                                        style={{
                                          borderTop: index === 0 ? '2px solid rgba(0,0,0,.1)' : 'none',
                                        }}
                                        >
                                            {index === 0 && <td rowSpan={entries.length + 1} data-label="subledger" style={{
                                                  borderRight: '1px solid rgba(34, 36, 38, 0.1)',
                                                }}>
                                                <div className="ui " style={{
                                                    padding: '5px', borderRadius: '5px',
                                                    textAlign: 'left',
                                                    fontSize: '1rem', textTransform: 'uppercase'
                                                }}>
                                                  <div style={{fontSize: '1.2rem', marginBottom: '.5rem'}}>{entry.subledgerType ? entry.subledgerType : entry.spendType}
                                                  {entry.feeCategory && <span> - {entry.feeCategory} Fee</span>}
                                                  </div>
                                                  <div style={{fontWeight: 'bold'}}>{subledgerId}</div>
                                                </div>
                                            </td>}
                                            <td data-label="date"
                                            >
                                                 <div className="ui" 
                                   
                                                style={{
                                                    padding: '5px', 
                                                    
                                                    fontSize: '1rem', textTransform: 'uppercase'
                                                }}>{toFormattedDate(entry.date)}</div>
                                            </td>
                                            <td data-label="balance">{toCurrency(entry.balance, 4)}</td>
                                            <td data-label="total">{toCurrency(entry.total, 4)}</td>
                                            <td data-label="accrued">{toCurrency(entry.accrued, 4)}</td>
                                            <td data-label="adjustment">{toCurrency(entry.adjustment, 4)}</td>
                                            <td data-label="penalty">{toCurrency(entry.penalty, 4)}</td>
                                            <td data-label="actions">
                                                <button className="ui button blue" onClick={() => openBreakdownsModal(entry.breakdowns, entry.penalty, `(${entry.date}) ${entry.subledgerType ? entry.subledgerType : entry.spendType} ${entry.feeCategory ? entry.feeCategory +' Fee ' : ''} - ${subledgerId}`)}>View Breakdown</button>
                                            </td>
                                        </tr>
                                         {(index === entries.length - 1) && <tr style={{fontWeight: 'bold', fontSize: '1.1rem'}}>
                                                <td>TOTAL</td>
                                                <td></td>
                                                <td>{toCurrency(entries.reduce((acc, entry) => acc + entry.total, 0), 4)}</td>
                                                <td>{toCurrency(entries.reduce((acc, entry) => acc + entry.accrued, 0), 4)}</td>
                                                <td>{toCurrency(entries.reduce((acc, entry) => acc + entry.adjustment, 0), 4)}</td>
                                                <td>{toCurrency(entries.reduce((acc, entry) => acc + entry.penalty, 0), 4)}</td>
                                            </tr>}
                                        </>

                                    ))
                                ))
                                : groupedByDate.map(dailyAccrual => (
                                    dailyAccrual.subledgers.map((subledger, index) => (
                                        <>
                                        <tr key={`${dailyAccrual.date}-${subledger.subledgerId}`}
                                        style={{
                                          borderTop: index === 0 ? '2px solid rgba(0,0,0,.1)' : 'none',
                                        }}
                                        >
                                            {index === 0 && <td 
                                            rowSpan={dailyAccrual.subledgers.length + 1} data-label="date" style={{
                                              borderRight: '1px solid rgba(34, 36, 38, 0.1)'
                                            }}>
                                                <h3>{toFormattedDate(dailyAccrual?.date)}</h3>
                                            </td>}
                                            <td data-label="subledger">
                                              <div>{subledger.subledgerType ? subledger.subledgerType : subledger.spendType}
                                              {subledger.feeCategory && <span> - {subledger.feeCategory} Fee</span>}
                                              </div>
                                              <div className="ui grey sub header" style={{marginTop: 0}}>{subledger.subledgerId}
                                              </div>
                                            </td>
                                            <td data-label="balance">{toCurrency(subledger?.dailyInterest?.balance, 4)}</td>
                                            <td data-label="total">{toCurrency(subledger?.dailyInterest?.total, 4)}</td>
                                            <td data-label="accrued">{toCurrency(subledger?.dailyInterest?.accrued, 4)}</td>
                                            <td data-label="adjustment">{toCurrency(subledger?.dailyInterest?.adjustment, 4)}</td>
                                            <td data-label="penalty">{toCurrency(subledger?.dailyInterest?.penalty, 4)}</td>
                                            <td data-label="actions">
                                                <button className="ui button blue" onClick={() => openBreakdownsModal(subledger?.breakdowns, subledger?.dailyInterest?.penalty,  ` (${dailyAccrual?.date}) ${subledger.spendType} ${subledger.feeCategory ? ' - ' + subledger.feeCategory + ' Fee ' : ''} - ${subledger.subledgerId}`)}>View Breakdown</button>
                                            </td>
                                        </tr>
                                        {index === dailyAccrual.subledgers.length - 1 && <tr style={{fontWeight: 'bold', fontSize: '1.1rem'}}>
                                            <td>TOTAL</td>
                                            <td></td>
                                            <td>{toCurrency(dailyAccrual.subledgers.reduce((acc, subledger) => acc + subledger.dailyInterest.total, 0), 4)}</td>
                                            <td>{toCurrency(dailyAccrual.subledgers.reduce((acc, subledger) => acc + subledger.dailyInterest.accrued, 0), 4)}</td>
                                            <td>{toCurrency(dailyAccrual.subledgers.reduce((acc, subledger) => acc + subledger.dailyInterest.adjustment, 0), 4)}</td>
                                            <td>{toCurrency(dailyAccrual.subledgers.reduce((acc, subledger) => acc + subledger.dailyInterest.penalty, 0), 4)}</td>
                                        </tr>}
                                        </>
                                    ))
                                ))}

                                
                        </tbody>
                    </table>
                </>
            )}
        </>
    );
}
